import { Avatar, Button, Card, Divider, Image, Rate, Space, Spin, Tag, Timeline, Tooltip } from "antd";
import {
  ClockCircleOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  CarOutlined,
  UserOutlined,
  PhoneOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  StarFilled,
  ArrowRightOutlined,
  LinkOutlined,
  MobileOutlined
} from "@ant-design/icons";
import useTripDetail from "hooks/trips/useTripDetail";
import moment from "moment";
import { useMemo } from "react";
import Status from "../Trip/components/Status";
import { RouteName } from "constants/route";
import { Link } from "react-router-dom";
import { isNotPolice } from "helpers/privilege";

export default function TripDetail() {
  const {
    data: { data, loading },
  } = useTripDetail();

  const duration = useMemo(() => {
    if (data?.started_at && data.ended_at) {
      const startedAt = moment(data.started_at);
      const endedAt = moment(data.ended_at);
      const duration = endedAt.diff(startedAt, "seconds");
      if (duration < 60) return `${duration.toFixed(0)} seconds`;
      else if (duration < 3600) return `${(duration / 60).toFixed(0)} minutes`;
      else return `${(duration / 3600).toFixed(1)} hours`;
    }
    return "N/A";
  }, [data?.started_at, data?.ended_at]);

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "green";
      case "cancelled":
        return "red";
      case "ongoing":
        return "blue";
      case "pending":
        return "orange";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return <CheckCircleOutlined />;
      case "cancelled":
        return <CloseCircleOutlined />;
      case "ongoing":
        return <ExclamationCircleOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50">
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <ExclamationCircleOutlined className="text-6xl text-gray-400 mb-4" />
          <p className="text-xl text-gray-600">Trip not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section with Status */}
        <Card className="shadow-md border-0">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                Trip Details
              </h1>
              <Space size="middle">
                <Tag
                  icon={getStatusIcon(data.status)}
                  color={getStatusColor(data.status)}
                  className="text-base px-3 py-1"
                >
                  {data.status?.toUpperCase()}
                </Tag>
                <Tag color="blue" className="text-base px-3 py-1">
                  {data.fleet_model.name}
                </Tag>
              </Space>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500 mb-1">Trip ID</div>
              <div className="font-mono text-sm font-semibold text-gray-700">
                {data.id.substring(0, 8)}...
              </div>
            </div>
          </div>
        </Card>

        {/* Trip Route Card */}
        <Card
          title={
            <span className="text-lg font-semibold flex items-center gap-2">
              <EnvironmentOutlined className="text-blue-500" />
              Route Information
            </span>
          }
          className="shadow-md border-0"
        >
          <div className="space-y-4">
            {/* Pickup Location */}
            <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex-shrink-0 mt-1">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                  <span className="text-white font-bold">A</span>
                </div>
              </div>
              <div className="flex-grow">
                <div className="text-xs font-semibold text-blue-600 uppercase mb-1">
                  Pickup Location
                </div>
                <div className="text-base font-medium text-gray-800">
                  {data.pickup_address}
                </div>
                {data.started_at && (
                  <div className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                    <ClockCircleOutlined />
                    {moment(data.started_at).format("MMM DD, YYYY • h:mm A")}
                  </div>
                )}
              </div>
            </div>

            {/* Connection Line */}
            <div className="flex items-center gap-4 px-4">
              <div className="flex-shrink-0 w-10 flex justify-center">
                <div className="w-0.5 h-12 bg-blue-300"></div>
              </div>
              <div className="flex-grow">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <CarOutlined />
                  <span className="font-medium">{duration}</span>
                  <span>•</span>
                  <span>{data.distance ? Number(data.distance).toFixed(2) : "N/A"} km</span>
                </div>
              </div>
            </div>

            {/* Drop-off Location */}
            <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg border border-green-100">
              <div className="flex-shrink-0 mt-1">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                  <span className="text-white font-bold">B</span>
                </div>
              </div>
              <div className="flex-grow">
                <div className="text-xs font-semibold text-green-600 uppercase mb-1">
                  Drop-off Location
                </div>
                <div className="text-base font-medium text-gray-800">
                  {data.drop_off_address}
                </div>
                {data.ended_at && (
                  <div className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                    <ClockCircleOutlined />
                    {moment(data.ended_at).format("MMM DD, YYYY • h:mm A")}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Route Action Buttons */}
          <Divider />
          <div className="flex flex-wrap gap-3">
            <Link to={RouteName.TRIP_ROUTE.replace(":id", data.id)}>
              <Button
                type="primary"
                icon={<LinkOutlined />}
                size="large"
                className="shadow-sm"
              >
                View Google Route
              </Button>
            </Link>
            <Link to={RouteName.TRIP_ROUTE_OLD.replace(":id", data.id)}>
              <Button
                icon={<EnvironmentOutlined />}
                size="large"
                className="shadow-sm"
              >
                View Route
              </Button>
            </Link>
            {isNotPolice() && (
              <>
                <Link to={RouteName.TRIP_ROUTE_ESTIMATION.replace(":id", data.id)}>
                  <Button
                    icon={<ArrowRightOutlined />}
                    size="large"
                    className="shadow-sm"
                  >
                    Estimation Route
                  </Button>
                </Link>
                <Link to={RouteName.TRIP_ROUTE_POINTS.replace(":id", data.id)}>
                  <Button
                    icon={<EnvironmentOutlined />}
                    size="large"
                    className="shadow-sm"
                  >
                    Route Points
                  </Button>
                </Link>
              </>
            )}
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Driver & Passenger Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Driver Information */}
            <Card
              title={
                <span className="text-lg font-semibold flex items-center gap-2">
                  <UserOutlined className="text-blue-500" />
                  Driver Information
                </span>
              }
              className="shadow-md border-0"
            >
              {data.driver && (
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <Avatar
                      size={120}
                      src={data.driver.image}
                      className="border-4 border-gray-200 shadow-md"
                    >
                      {!data.driver.image && (
                        <UserOutlined className="text-4xl" />
                      )}
                    </Avatar>
                  </div>
                  <div className="flex-grow space-y-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-1">
                        {data.driver.first_name} {data.driver.last_name}
                      </h3>
                      <div className="flex items-center gap-2 mb-2">
                        <Rate
                          value={data.driver.rate}
                          allowHalf
                          disabled
                          className="text-yellow-500"
                        />
                        <span className="text-sm text-gray-600">
                          {data.driver.rate?.toFixed(1)}
                        </span>
                      </div>
                    </div>
                    <Divider className="my-3" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <PhoneOutlined className="text-blue-500 text-lg" />
                        <div>
                          <div className="text-xs text-gray-500">Phone</div>
                          <div className="font-medium text-gray-800">
                            {data.driver.phone}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <CarOutlined className="text-blue-500 text-lg" />
                        <div>
                          <div className="text-xs text-gray-500">Vehicle Type</div>
                          <div className="font-medium text-gray-800">
                            {data.fleet_model.name}
                          </div>
                        </div>
                      </div>
                      {data.driver.app_version && (
                        <div className="flex items-center gap-3">
                          <MobileOutlined className="text-blue-500 text-lg" />
                          <div>
                            <div className="text-xs text-gray-500">App Version</div>
                            <div className="font-medium text-gray-800">
                              {data.driver.app_version}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {(data.admin_compete || data.driver_compete || data.status_changed_by_admin) && (
                      <>
                        <Divider className="my-3" />
                        <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                          { (data.admin_compete || data.driver_compete) && (
                            <div>
                               <div className="text-xs text-gray-500 mb-1 font-semibold">Completed By</div>
                               <div className="flex items-center gap-2 font-medium text-gray-800">
                                 {data.admin_compete ? (
                                   <div className="flex items-center gap-2">
                                     <Avatar size={24} src={data.admin_compete.image} icon={<UserOutlined />} />
                                     <span>{`${data.admin_compete.first_name} ${data.admin_compete.last_name}`} <Tag color="gold" className="ml-1 uppercase text-[10px]">Admin</Tag></span>
                                   </div>
                                 ) : (
                                   <div className="flex items-center gap-2 text-blue-600 italic">
                                     <UserOutlined />
                                      <span>{`${data.driver_compete?.first_name} ${data.driver_compete?.last_name}`} <Tag color="blue" className="ml-1 uppercase text-[10px]">Driver</Tag></span>
                                   </div>
                                 )}
                               </div>
                            </div>
                          )}
                          {data.status_changed_by_admin && (
                             <div>
                               <div className="text-xs text-gray-500 mb-1 font-semibold">Status Override By</div>
                               <div className="flex items-center gap-2 font-medium text-gray-800">
                                  <Avatar size={24} src={data.status_changed_by_admin.image} icon={<UserOutlined />} />
                                  <span>{`${data.status_changed_by_admin.first_name} ${data.status_changed_by_admin.last_name}`} <Tag color="gold" className="ml-1 uppercase text-[10px]">Admin</Tag></span>
                               </div>
                             </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </Card>

            {/* Passenger Information */}
            {data.passenger && (
              <Card
                title={
                  <span className="text-lg font-semibold flex items-center gap-2">
                    <UserOutlined className="text-purple-500" />
                    Passenger Information
                  </span>
                }
                className="shadow-md border-0"
              >
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <Avatar
                      size={120}
                      src={data.passenger.image}
                      className="border-4 border-gray-200 shadow-md"
                    >
                      {!data.passenger.image && (
                        <UserOutlined className="text-4xl" />
                      )}
                    </Avatar>
                  </div>
                  <div className="flex-grow space-y-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-1">
                        {data.passenger.first_name} {data.passenger.last_name}
                      </h3>
                      <div className="flex items-center gap-2 mb-2">
                        <Rate
                          value={data.passenger.rate}
                          allowHalf
                          disabled
                          className="text-yellow-500"
                        />
                        <span className="text-sm text-gray-600">
                          {data.passenger.rate?.toFixed(1)}
                        </span>
                      </div>
                    </div>
                    <Divider className="my-3" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <PhoneOutlined className="text-purple-500 text-lg" />
                        <div>
                          <div className="text-xs text-gray-500">Phone</div>
                          <div className="font-medium text-gray-800">
                            {data.passenger.phone}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <DollarOutlined className="text-green-500 text-lg" />
                        <div>
                          <div className="text-xs text-gray-500">Paid Amount</div>
                          <div className="font-medium text-gray-800">
                            {data.payment.total_price?.toFixed(2)} Br
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Right Column - Pricing & Timeline */}
          <div className="space-y-6">
            {/* Pricing Card */}
            <Card
              title={
                <span className="text-lg font-semibold flex items-center gap-2">
                  <DollarOutlined className="text-green-500" />
                  Trip Pricing
                </span>
              }
              className="shadow-md border-0"
            >
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Base Fare</span>
                  <span className="font-semibold text-gray-800">
                    {data.payment.base_price?.toFixed(2)} Br
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Distance (km_price)</span>
                  <span className="font-semibold text-gray-800">
                    {data.payment.km_price?.toFixed(2)} Br
                  </span>
                </div>
                {data.payment.price_per_minute !== undefined && data.payment.price_per_minute !== null && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Price Per Minute</span>
                    <span className="font-semibold text-gray-800">
                      {Number(data.payment.price_per_minute).toFixed(5)} Br
                    </span>
                  </div>
                )}
                {data.payment.price_per_kilo_meter !== undefined && data.payment.price_per_kilo_meter !== null && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Price Per Km</span>
                    <span className="font-semibold text-gray-800">
                      {Number(data.payment.price_per_kilo_meter).toFixed(5)} Br
                    </span>
                  </div>
                )}
                {data.payment.cancellation_rate_price !== undefined && data.payment.cancellation_rate_price !== null && data.payment.cancellation_rate_price > 0 && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Cancellation Rate Price</span>
                    <span className="font-semibold text-gray-800">
                      {Number(data.payment.cancellation_rate_price).toFixed(5)} Br
                    </span>
                  </div>
                )}
                {data.payment.high_demand_rate_price !== undefined && data.payment.high_demand_rate_price !== null && data.payment.high_demand_rate_price > 0 && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">High Demand Rate Price</span>
                    <span className="font-semibold text-gray-800">
                      {Number(data.payment.high_demand_rate_price).toFixed(5)} Br
                    </span>
                  </div>
                )}
                {data.payment.working_time_rate_price !== undefined && data.payment.working_time_rate_price !== null && data.payment.working_time_rate_price > 0 && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Working Time Rate Price</span>
                    <span className="font-semibold text-gray-800">
                      {Number(data.payment.working_time_rate_price).toFixed(5)} Br
                    </span>
                  </div>
                )}
                <Divider className="my-2" />
                <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg border-2 border-green-200">
                  <span className="text-lg font-bold text-gray-800">Total Price</span>
                  <span className="text-2xl font-bold text-green-600">
                    {data.payment.total_price?.toFixed(2)} Br
                  </span>
                </div>
                {data.price && (
                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <span className="text-sm font-semibold text-yellow-800">Total Amount</span>
                    <span className="text-lg font-bold text-yellow-700">
                      {Number(data.price).toFixed(2)} Br
                    </span>
                  </div>
                )}

                {/* Dispatcher Pricing Details (Markup/Reward) */}
                {data.note?.dispatcher_pricing_applied && (
                  <div className="mt-4 p-3 border border-dashed border-blue-200 rounded-lg bg-blue-50/30">
                    <div className="text-[10px] font-bold text-blue-500 uppercase mb-2 tracking-wider flex items-center gap-1">
                      <ExclamationCircleOutlined /> Tiered Pricing Applied
                    </div>
                    <div className="space-y-2">
                       {data.note?.original_price !== undefined && (
                         <div className="flex justify-between text-xs text-gray-500 italic">
                            <span>Base Calculation</span>
                            <span>{data.note.original_price.toFixed(2)} Br</span>
                         </div>
                       )}
                       {data.note?.markup_amount !== undefined && data.note.markup_amount > 0 && (
                         <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Booking Markup</span>
                            <span className="font-semibold text-red-500">+{data.note.markup_amount.toFixed(2)} Br</span>
                         </div>
                       )}
                       {data.note?.passenger_reward_amount !== undefined && data.note.passenger_reward_amount > 0 && (
                         <div className="flex justify-between text-sm py-1 border-t border-blue-100 mt-1">
                            <span className="text-green-600 font-medium">Passenger Cashback</span>
                            <span className="font-bold text-green-600">-{data.note.passenger_reward_amount.toFixed(2)} Br</span>
                         </div>
                       )}
                       {data.note?.original_base_fare !== undefined && data.note?.dispatcher_pricing_applied === "driver_manual_base_fare" && (
                         <div className="flex justify-between text-xs py-1 border-t border-blue-100 mt-1">
                            <span className="text-blue-600 font-medium">Manual Fare Override</span>
                            <span className="font-bold text-blue-600">Applied</span>
                         </div>
                       )}
                       <div className="text-[9px] text-gray-400 mt-1">
                          Logic: {data.note.dispatcher_pricing_applied}
                       </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Trip Information Card */}
            <Card
              title={
                <span className="text-lg font-semibold flex items-center gap-2">
                  <ClockCircleOutlined className="text-orange-500" />
                  Trip Information
                </span>
              }
              className="shadow-md border-0"
            >
              <div className="space-y-3">
                {data.type && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Type</span>
                    <span className="font-semibold text-gray-800 capitalize">
                      {data.type}
                    </span>
                  </div>
                )}
                {data.waiting_time !== undefined && data.waiting_time !== null && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Waiting Time</span>
                    <span className="font-semibold text-gray-800">
                      {data.waiting_time} min
                    </span>
                  </div>
                )}
                {data.available_seats !== undefined && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Available Seats</span>
                    <span className="font-semibold text-gray-800">
                      {data.available_seats}
                    </span>
                  </div>
                )}
                {data.seats_booked !== undefined && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Seats Booked</span>
                    <span className="font-semibold text-gray-800">
                      {data.seats_booked}
                    </span>
                  </div>
                )}
                {data.min_passenger !== undefined && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Min Passengers</span>
                    <span className="font-semibold text-gray-800">
                      {data.min_passenger}
                    </span>
                  </div>
                )}
                {data.status && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Status</span>
                    <div>{Status(data.status)}</div>
                  </div>
                )}
                {data.started_at && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Started At</span>
                    <span className="font-semibold text-gray-800 text-xs">
                      {moment(data.started_at).format("MMM DD, YYYY • h:mm A")}
                    </span>
                  </div>
                )}
                {data.ended_at && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Ended At</span>
                    <span className="font-semibold text-gray-800 text-xs">
                      {moment(data.ended_at).format("MMM DD, YYYY • h:mm A")}
                    </span>
                  </div>
                )}
                {data.distance !== undefined && data.distance !== null && (
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Trip Distance</span>
                    <span className="font-semibold text-gray-800">
                      {Number(data.distance).toFixed(3)} KM
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Trip Duration</span>
                  <span className="font-semibold text-gray-800">
                    {duration}
                  </span>
                </div>
              </div>
            </Card>

            {/* Fleet Model Pricing Card */}
            <Card
              title={
                <span className="text-lg font-semibold flex items-center gap-2">
                  <CarOutlined className="text-indigo-500" />
                  Fleet Model Pricing
                </span>
              }
              className="shadow-md border-0"
            >
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-600">Fleet Name</span>
                  <span className="font-semibold text-gray-800">
                    {data.fleet_model.name}
                  </span>
                </div>
                <Divider className="my-2" />
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Regular Pricing</div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm text-gray-600">Base Fare</span>
                  <span className="font-semibold text-gray-800">
                    {data.fleet_model.base_fare} Br
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm text-gray-600">Price Per Kilo Meter</span>
                  <span className="font-semibold text-gray-800">
                    {data.fleet_model.price_per_kilo_meter} Br
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="text-sm text-gray-600">Price Per Minute</span>
                  <span className="font-semibold text-gray-800">
                    {data.fleet_model.price_per_minute} Br
                  </span>
                </div>
                <Divider className="my-2" />
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Special Pricing</div>
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm text-gray-600">Special Base Fare</span>
                  <span className="font-semibold text-gray-800">
                    {data.fleet_model.special_base_fare} Br
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm text-gray-600">Special Price Per Km</span>
                  <span className="font-semibold text-gray-800">
                    {data.fleet_model.special_price_per_kilo_meter} Br
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                  <span className="text-sm text-gray-600">Special Price Per Minute</span>
                  <span className="font-semibold text-gray-800">
                    {data.fleet_model.special_price_per_minute} Br
                  </span>
                </div>
              </div>
            </Card>

            {/* Trip Timeline */}
            <Card
              title={
                <span className="text-lg font-semibold flex items-center gap-2">
                  <CalendarOutlined className="text-blue-500" />
                  Trip Timeline
                </span>
              }
              className="shadow-md border-0"
            >
              <Timeline
                items={[
                  {
                    color: "green",
                    children: (
                      <div>
                        <div className="font-semibold text-gray-800">Trip Created</div>
                        <div className="text-sm text-gray-500">
                          {moment(data.created_at).format("MMM DD, YYYY • h:mm A")}
                        </div>
                      </div>
                    ),
                  },
                  ...(data.started_at ? [{
                    color: "blue" as const,
                    children: (
                      <div>
                        <div className="font-semibold text-gray-800">Trip Started</div>
                        <div className="text-sm text-gray-500">
                          {moment(data.started_at).format("MMM DD, YYYY • h:mm A")}
                        </div>
                      </div>
                    ),
                  }] : []),
                  ...(data.ended_at ? [{
                    color: "green" as const,
                    children: (
                      <div>
                        <div className="font-semibold text-gray-800">Trip Completed</div>
                        <div className="text-sm text-gray-500">
                          {moment(data.ended_at).format("MMM DD, YYYY • h:mm A")}
                        </div>
                      </div>
                    ),
                  }] : []),
                  ...(data.canceled_at ? [{
                    color: "red" as const,
                    children: (
                      <div>
                        <div className="font-semibold text-gray-800">Trip Cancelled</div>
                        <div className="text-sm text-gray-500">
                          {moment(data.canceled_at).format("MMM DD, YYYY • h:mm A")}
                          {data.reason && (
                            <div className="mt-1">Reason: {data.reason}</div>
                          )}
                        </div>
                      </div>
                    ),
                  }] : []),
                ]}
              />
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}